<template>
  <div v-loading="loading" class="nx-page nx-report-page mult-transport-statistics">
    <nx-report-header title="多维度运输数据智能统计">
      <div class="mts-toolbar" style="margin: 0; padding: 0; background: transparent; border: 0">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          size="small"
          :clearable="false"
          :picker-options="pickerOptions"
          style="width: 280px"
        />
        <el-button type="primary" size="small" icon="el-icon-data-analysis" @click="loadData">分析</el-button>
      </div>
    </nx-report-header>
    <div class="mts-charts-grid">
      <div class="mts-chart-card">
        <div class="mts-chart-head">货主分布（派车数占比）</div>
        <div ref="ownerPie" class="mts-chart-box" />
      </div>
      <div class="mts-chart-card">
        <div class="mts-chart-head">煤种分布（到货量占比）</div>
        <div ref="coalPie" class="mts-chart-box" />
      </div>
      <div class="mts-chart-card">
        <div class="mts-chart-head">各货主汽运入库量与入库车数对比</div>
        <div ref="compareChart" class="mts-chart-box" />
      </div>
      <div class="mts-chart-card">
        <div class="mts-chart-head">日计划完成率趋势</div>
        <div ref="dailyRateChart" class="mts-chart-box" />
      </div>
      <div class="mts-chart-card mts-full">
        <div class="mts-chart-head">各货主提报计划完成率</div>
        <div ref="ownerRateChart" class="mts-chart-box mts-tall" />
      </div>
      <div class="mts-chart-card mts-full">
        <div class="mts-chart-head">各货主车辆平均在厂时长</div>
        <div ref="stayChart" class="mts-chart-box mts-tall" />
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchMultTransportStatistics } from './api'
import { getMonthStartTime, getTodayEndTime } from '@/util/date.js'
import { getDefaultSearchTimeRange, getSearchTimePickerOptions } from '@/basic-common/util/common'

export default {
  name: 'MultTransportDataStatistics',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      dateRange: getDefaultSearchTimeRange('daterange'),
      pickerOptions: getSearchTimePickerOptions('daterange'),
      chartInstances: [],
      payloadCache: null
    }
  },
  mounted() {
    const s = (getMonthStartTime() || '').slice(0, 10)
    const e = (getTodayEndTime() || '').slice(0, 10)
    this.dateRange = s && e ? [s, e] : getDefaultSearchTimeRange('daterange')
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.disposeCharts()
  },
  methods: {
    disposeCharts() {
      this.chartInstances.forEach((c) => c && c.dispose())
      this.chartInstances = []
    },
    onResize() {
      this.chartInstances.forEach((c) => c && c.resize())
    },
    pieData(list) {
      return (list || []).map((x) => ({
        name: x.name || '未知',
        value: Number(x.amount != null ? x.amount : 0)
      }))
    },
    async loadData() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning('请选择日期区间')
        return
      }
      this.loading = true
      try {
        const data = await fetchMultTransportStatistics({
          startDate: this.dateRange[0],
          endDate: this.dateRange[1]
        })
        this.payloadCache = data
        this.$nextTick(() => this.renderCharts(data))
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    renderCharts(data) {
      this.disposeCharts()
      const ownerPie = echarts.init(this.$refs.ownerPie)
      const coalPie = echarts.init(this.$refs.coalPie)
      const compare = echarts.init(this.$refs.compareChart)
      const dailyRate = echarts.init(this.$refs.dailyRateChart)
      const ownerRate = echarts.init(this.$refs.ownerRateChart)
      const stay = echarts.init(this.$refs.stayChart)
      this.chartInstances = [ownerPie, coalPie, compare, dailyRate, ownerRate, stay]

      const ownerDispatch = this.pieData(data.ownerDispatchPie)
      const coalArrival = this.pieData(data.coalArrivalPie)

      ownerPie.setOption({
        tooltip: { trigger: 'item' },
        legend: { orient: 'vertical', left: 'left', textStyle: { color: '#334155' } },
        series: [
          {
            type: 'pie',
            radius: '55%',
            data: ownerDispatch,
            label: { show: true, formatter: '{b}: {d}%' }
          }
        ]
      })

      coalPie.setOption({
        tooltip: { trigger: 'item' },
        legend: { orient: 'vertical', left: 'left', textStyle: { color: '#334155' } },
        series: [
          {
            type: 'pie',
            radius: '55%',
            data: coalArrival,
            label: { show: true, formatter: '{b}: {d}%' }
          }
        ]
      })

      const oc = data.ownerCompare || []
      const names = oc.map((r) => r.custName || '未知')
      const tons = oc.map((r) => Number(r.netWeightSum != null ? r.netWeightSum : 0))
      const trucks = oc.map((r) => Number(r.truckCount != null ? r.truckCount : 0))

      compare.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { data: ['入库量(吨)', '入库车数(车次)'], textStyle: { color: '#334155' } },
        xAxis: { type: 'category', data: names, axisLabel: { rotate: 30, color: '#475569' } },
        yAxis: [
          { type: 'value', name: '入库量 (吨)', nameTextStyle: { fontSize: 12 } },
          { type: 'value', name: '车次', nameTextStyle: { fontSize: 12 } }
        ],
        series: [
          {
            name: '入库量(吨)',
            type: 'bar',
            data: tons,
            itemStyle: { color: '#3b82f6', borderRadius: [6, 6, 0, 0] },
            barWidth: '40%',
            label: { show: true, position: 'top' }
          },
          {
            name: '入库车数(车次)',
            type: 'line',
            data: trucks,
            yAxisIndex: 1,
            lineStyle: { color: '#f97316', width: 2 },
            symbol: 'circle',
            symbolSize: 8,
            label: { show: true, position: 'top' }
          }
        ]
      })

      const trend = data.dailyRateTrend || []
      const days = trend.map((r) => r.statDay)
      const rates = trend.map((r) => Number(r.ratePercent != null ? r.ratePercent : 0))
      const yMax = Math.max(100, ...rates, 0)

      dailyRate.setOption({
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: days, axisLabel: { rotate: 30, color: '#475569' } },
        yAxis: { type: 'value', name: '完成率 (%)', min: 0, max: yMax },
        series: [
          {
            type: 'line',
            data: rates,
            smooth: true,
            lineStyle: { color: '#f97316', width: 3 },
            areaStyle: { opacity: 0.1 },
            symbol: 'circle',
            symbolSize: 6,
            label: { show: true, position: 'top', fontSize: 10, formatter: (p) => (p.value != null ? Number(p.value).toFixed(1) : '') }
          }
        ]
      })

      const opr = [...(data.ownerPlanCompleteRate || [])].sort(
        (a, b) => Number(b.ratePercent || 0) - Number(a.ratePercent || 0)
      )
      ownerRate.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const p = params[0]
            return `${p.name}<br/>完成率: ${Number(p.value).toFixed(1)}%`
          }
        },
        grid: { containLabel: true, left: '12%', top: 20, bottom: 20 },
        xAxis: { type: 'value', name: '计划完成率 (%)', max: 100 },
        yAxis: { type: 'category', data: opr.map((r) => r.custName || '未知'), axisLabel: { fontSize: 11 } },
        series: [
          {
            type: 'bar',
            data: opr.map((r) => Number(r.ratePercent != null ? r.ratePercent : 0)),
            itemStyle: { color: '#10b981', borderRadius: [0, 6, 6, 0] },
            label: { show: true, position: 'right', formatter: (p) => `${Number(p.value).toFixed(1)}%` }
          }
        ]
      })

      const stayRows = data.ownerAvgStayMinutes || []
      stay.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        xAxis: {
          type: 'category',
          data: stayRows.map((r) => r.custName || '未知'),
          axisLabel: { rotate: 30, color: '#475569' }
        },
        yAxis: { type: 'value', name: '平均在厂时长 (分钟)' },
        series: [
          {
            type: 'bar',
            data: stayRows.map((r) => Number(r.avgStayMinutes != null ? r.avgStayMinutes : 0)),
            itemStyle: { color: '#8b5cf6', borderRadius: [6, 6, 0, 0] },
            label: { show: true, position: 'top', formatter: (p) => (p.value != null ? Number(p.value).toFixed(1) : '') }
          }
        ]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
